import type { GrowthBook } from '@growthbook/growthbook-react';

export const EARLY_ADOPTER_FLAG = 'feature.early-adopter';

const FLAG_PREFIX = 'feature.';
const STORAGE_KEY = 'earlyAdopterOverrides';

export type FeatureOverrides = Record<string, boolean>;

const isOverridable = (key: string) => key.startsWith(FLAG_PREFIX) && key !== EARLY_ADOPTER_FLAG;

const parseQueryValue = (value: string) => {
  if (value === '' || value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

export const readQueryOverrides = (search: string): FeatureOverrides => {
  const overrides: FeatureOverrides = {};
  new URLSearchParams(search).forEach((value, key) => {
    const parsed = parseQueryValue(value);
    if (isOverridable(key) && parsed !== undefined) overrides[key] = parsed;
  });
  return overrides;
};

const readStoredOverrides = (): FeatureOverrides => {
  try {
    const stored: unknown = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.fromEntries(
      Object.entries(stored).filter(
        (entry): entry is [string, boolean] =>
          isOverridable(entry[0]) && typeof entry[1] === 'boolean'
      )
    );
  } catch {
    return {};
  }
};

const storeOverrides = (overrides: FeatureOverrides) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    return true;
  } catch {
    return false;
  }
};

export const collectFeatureOverrides = (search: string): FeatureOverrides => {
  const overrides = { ...readStoredOverrides(), ...readQueryOverrides(search) };
  storeOverrides(overrides);
  return overrides;
};

export const readAllowedFlags = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((key): key is string => typeof key === 'string' && isOverridable(key))
    : [];

export const applyFeatureOverrides = (
  gb: GrowthBook,
  overrides: FeatureOverrides,
  allowedFlags: string[]
) => {
  gb.setForcedFeatures(
    new Map(Object.entries(overrides).filter(([key]) => allowedFlags.includes(key)))
  );
};
